import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Info, Eye, EyeOff, RotateCcw, ChevronUp, ChevronDown } from 'lucide-react';
import moment from 'moment';
import TagCostInfoModal from '../../components/common/TagCostInfoModal';
import CommonTable from '../../Common/CommonTable';
import { getVehicleClassData } from '../../Utils/helper';
import { vehicleClasses } from '../../Utils/Constants';
import TextInput from '../../Commponent/TextInput';
import CustomSelectInput from '../../Commponent/SelectInput';
import { API } from '../../Common/Api';
import { ErrorToast, SuccessToast } from '../../Utils/toast';

// Distributor Master FASTag — the complete lifecycle & hierarchy
// (Distributor -> Dealer -> Agent -> Activation) for every FASTag that belongs
// to the logged-in distributor's tree. Same idea as the Admin master page, but
// the backend scopes the results to this distributor only.
const DistributorMasterFASTag = () => {
  const navigate = useNavigate();

  // Data / pagination
  const [inventoryData, setInventoryData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selectedVehicleClass, setSelectedVehicleClass] = useState('');
  const [selectedDealer, setSelectedDealer] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [activationFilter, setActivationFilter] = useState(''); // FASTag Activation status
  const [activationSortOrder, setActivationSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first

  // Filter option sources
  const [dealerNames, setDealerNames] = useState([]);
  const [agentNames, setAgentNames] = useState([]);

  // Vehicle-image modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedClass, setSelectedClass] = useState({});
  const [visibleRows, setVisibleRows] = useState({});
  const [selectedTagRefId, setSelectedTagRefId] = useState(null);
  const [selectedTagActivated, setSelectedTagActivated] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState('cost');

  const isRowActivated = (item) => item?.isActivated === true || item?.userActivationStatus === 'Active';

  const statusOptions = [
    { name: 'All Status', value: '' },
    { name: 'Unassigned', value: 'Unassigned' },
    { name: 'Assigned', value: 'Assigned' },
  ];

  const toggleVisibility = (index, row) => {
    const isVisible = visibleRows[index];
    setVisibleRows({ ...visibleRows, [index]: !isVisible });
    setModalOpen(!isVisible);
    if (!isVisible) {
      setSelectedClass(row);
      setSelectedIndex(index);
    }
  };

  // ---- Data fetching -------------------------------------------------------
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(status && { status }),
        ...(selectedVehicleClass && { VC: selectedVehicleClass }),
        ...(selectedDealer && { dealer: selectedDealer }),
        ...(selectedAgent && { agent: selectedAgent }),
        ...(activationFilter && { isActivated: activationFilter }),
        // Sort by newest/oldest activation date first when "Activated" filter is selected.
        ...(activationFilter === 'true' && { activationSort: 'true', activationSortOrder }),
        ...(selectedDate && {
          startDate: moment(selectedDate).format('YYYY-MM-DD'),
          endDate: moment(selectedDate).format('YYYY-MM-DD'),
        }),
      };

      const response = await API.GetDistributorMasterFastag(params);

      if (response?.status === 200 && response?.success) {
        const records = Array.isArray(response.data) ? response.data : [];
        setInventoryData(records);
        const total = response.pagination?.total || 0;
        setTotalRecords(total);
        setTotalPages(response.pagination?.totalPages || Math.max(1, Math.ceil(total / itemsPerPage)));
      } else {
        setInventoryData([]);
        setTotalRecords(0);
        setTotalPages(1);
        if (response?.message) ErrorToast(response.message);
      }
    } catch (err) {
      setInventoryData([]);
      setTotalRecords(0);
      setTotalPages(1);
      ErrorToast('Failed to fetch master FASTag inventory');
    } finally {
      setIsLoading(false);
    }
  };

  // Load dealer & agent filter options once.
  const loadFilterOptions = async () => {
    try {
      const [dealersRes, agentsRes] = await Promise.all([
        API.GetDealers({ page: 1, limit: 1000 }),
        API.GetAgentsNames(),
      ]);

      const dealers = dealersRes?.data?.data || dealersRes?.data || [];
      setDealerNames(Array.isArray(dealers) ? dealers : []);

      const agents = agentsRes?.data || [];
      setAgentNames(Array.isArray(agents) ? agents : []);
    } catch (err) {
      // Filters are optional; ignore load errors.
    }
  };

  // Cascade: selecting a dealer scopes the Agent dropdown to that dealer's agents.
  const handleDealerChange = (dealerId) => {
    setSelectedDealer(dealerId);
    setSelectedAgent(''); // reset agent
    setCurrentPage(1);
    API.GetAgentsNames(dealerId ? { dealerId } : undefined)
      .then((res) => setAgentNames(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setAgentNames([]));
  };

  // Debounce search input.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 800);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, selectedDate, debouncedSearch, status, selectedVehicleClass, selectedDealer, selectedAgent, activationFilter, activationSortOrder]);

  // ---- Download ------------------------------------------------------------
  // Dedicated to the download button: the screen's other loading flags drive
  // the table overlay and a refetch, which a download must not trigger.
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const params = {
        search: debouncedSearch,
        status,
        VC: selectedVehicleClass,
        dealer: selectedDealer,
        agent: selectedAgent,
        // Was missing — the on-screen list applies this filter (line ~82) but
        // the download silently ignored it, exporting every activation status
        // regardless of what "Select Status" was set to.
        isActivated: activationFilter,
        startDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
        endDate: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '',
      };
      const blob = await API.ExportDistributorMasterInventory(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `distributor-master-fastag-${moment().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      SuccessToast('File downloaded successfully');
    } catch (err) {
      ErrorToast('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  // ---- Presentational helpers ---------------------------------------------
  const StatusBadge = ({ status }) => {
    const base = 'px-2 py-[2px] rounded-full text-xs font-medium flex items-center gap-1 justify-self-start';
    if (status === 'Assigned')
      return <span className={`${base} bg-green-100 text-green-900`}><span className="h-2 w-2 bg-green-500 rounded-full" />Assigned</span>;
    if (status === 'Unassigned')
      return <span className={`${base} bg-yellow-100 text-yellow-900`}><span className="h-2 w-2 bg-yellow-500 rounded-full" />Unassigned</span>;
    return <span className="text-xs text-gray-500">N/A</span>;
  };

  const ActivationBadge = ({ active }) => {
    const base = 'px-2 py-[2px] rounded-full text-xs font-medium flex items-center gap-1 justify-self-start';
    if (active === true || active === 'Active')
      return <span className={`${base} bg-green-100 text-green-900`}><span className="h-2 w-2 bg-green-500 rounded-full" />Active</span>;
    return <span className={`${base} bg-yellow-100 text-yellow-900`}><span className="h-2 w-2 bg-yellow-500 rounded-full" />Inactive</span>;
  };

  // Where the tag currently sits in the hierarchy.
  const currentPosition = (item) => {
    if (item?.EndStatusActivation === true || item?.EndStatusActivation === 'Active' || item?.userActivationDate) return 'Activated';
    if (item?.agentStatus === 'Assigned') return 'With Agent';
    if (item?.assignedToDealerID || item?.assignedTo?.role === 'dealer') return 'With Dealer';
    return 'With Distributor';
  };

  const PositionBadge = ({ item }) => {
    const pos = currentPosition(item);
    const map = {
      'Activated': 'bg-emerald-100 text-emerald-900',
      'With Agent': 'bg-blue-100 text-blue-900',
      'With Dealer': 'bg-purple-100 text-purple-900',
      'With Distributor': 'bg-gray-100 text-gray-800',
    };
    return <span className={`px-2 py-[2px] rounded-full text-xs font-medium ${map[pos]}`}>{pos}</span>;
  };

  const getDealer = (item) => {
    if (item?.assignedToDealerID) return item.assignedToDealerID;
    if (item?.assignedTo?.role === 'dealer') return item.assignedTo;
    if (item?.agentAssignedBy?.role === 'dealer') return item.agentAssignedBy;
    return null;
  };

  const vehicleData = getVehicleClassData(selectedClass.vehicleClass);

    // Date over time on two lines — same shape the other inventory tables use.
  const ShortDate = ({ value }) => {
    if (!value || !moment(value).isValid()) return 'N/A';
    const m = moment(value).local();
    return (
      <div className="leading-tight">
        <div>{m.format('DD MMM YYYY')}</div>
        <div className="text-xs text-gray-400">{m.format('hh:mm A')}</div>
      </div>
    );
  };

const columns = [
    {
      label: 'Tag Ref ID',
      key: 'tagRefId',
      render: (item) => {
        const tagVal = item.tagRefId || item.serialNumber || '';
        const activated = isRowActivated(item);
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span>{tagVal}</span>
              {tagVal && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedTagRefId(tagVal); setSelectedTagActivated(activated); setModalInitialTab('cost'); }}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-red-500 focus:outline-none"
                  title="View pricing details"
                >
                  <Info size={14} />
                </button>
              )}
            </div>
            {tagVal && activated && (
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedTagRefId(tagVal); setSelectedTagActivated(true); setModalInitialTab('transactions'); }}
                className="text-xs font-medium text-blue-600 hover:underline text-left w-fit"
              >
                View Transactions
              </button>
            )}
          </div>
        );
      },
    },
    { label: 'Batch ID', key: 'batchId' },
    {
      label: 'Vehicle Class',
      key: 'vehicleClass',
      render: (item, idx) => {
        const vc = getVehicleClassData(item.vehicleClass);
        return (
          <div className="flex gap-2 items-center">
            <img src={vc?.tagimage} alt="Tag" className="w-[34px]" />
            <p>{vc?.value || item.vehicleClass}</p>
            <div className="pl-2 flex items-center cursor-pointer" onClick={() => toggleVisibility(idx, item)}>
              {visibleRows[idx] ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </div>
          </div>
        );
      },
    },
    { label: 'Current Position', render: (item) => <PositionBadge item={item} /> },
    {
      label: 'Dealer',
      render: (item) => getDealer(item)?.username || 'N/A',
    },
    {
      label: 'Dealer Assigned',
      render: (item) => {
        const dealer = getDealer(item);
        return dealer && item.assignedDate ? <ShortDate value={item.assignedDate} /> : 'N/A';
      },
    },
    { label: 'Agent Name', render: (item) => item.agentAssignedTo?.username || 'N/A' },
    {
      label: 'Agent Assigned',
      render: (item) =>
        item.agentAssignedDate ? <ShortDate value={item.agentAssignedDate} /> : 'N/A',
    },
    {
      label: 'Agent Status',
      tdClassName: 'flex justify-end',
      render: (item) => <StatusBadge status={item.agentStatus} />,
    },
    { label: 'User Mobile', render: (item) => item?.userMobileNumber || 'N/A' },
    {
      label: (
        <div className="flex items-center gap-1">
          <span>Activated</span>
          {activationFilter === 'true' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActivationSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
                setCurrentPage(1);
              }}
              title={activationSortOrder === 'desc' ? 'Newest activation first (click for oldest first)' : 'Oldest activation first (click for newest first)'}
              className="ml-0.5 text-[#EE3B31] hover:text-red-700 transition-colors focus:outline-none"
            >
              {activationSortOrder === 'desc' ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronUp className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </div>
      ),
      render: (item) =>
        item.userActivationDate ? <ShortDate value={item.userActivationDate} /> : 'N/A',
    },
    {
      label: 'User Status',
      tdClassName: 'flex justify-end',
      render: (item) => <ActivationBadge active={item?.EndStatusActivation} />,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 mt-2">
        <button
          className="back-arrow leading-none"
          style={{ fontSize: '20px', color: '#ff3f34', cursor: 'pointer', fontWeight: 'bold' }}
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <h1 className="text-xl font-bold text-gray-800 leading-tight">Master FASTag Inventory</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm px-4 py-3 mb-3">
        <div className="flex flex-col xl:flex-row xl:items-end gap-2.5">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2.5 flex-1 min-w-0">
          <div className="relative">
            <TextInput
              name="search"
              placeholder="TagRefId / Batch ID"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              inputClass="pl-3 pr-9 py-2 text-sm"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </div>
          </div>

          <CustomSelectInput
            name="status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
            options={statusOptions}
            inputClass="py-2 px-3 text-sm"
          />

          <CustomSelectInput
            name="vehicleClass"
            value={selectedVehicleClass}
            onChange={(e) => { setSelectedVehicleClass(e.target.value); setCurrentPage(1); }}
            options={[
              { name: 'All Vehicle Classes', value: '' },
              ...vehicleClasses.map((v) => ({ ...v, name: v.value, value: v.value })),
            ]}
            inputClass="py-2 px-3 text-sm"
          />

          {/* FASTag Activation Status Filter */}
          <CustomSelectInput
            name="activationFilter"
            value={activationFilter}
            onChange={(e) => { setActivationFilter(e.target.value); setCurrentPage(1); }}
            options={[
              { name: 'Select Status', value: '' },
              { name: 'Activated', value: 'true' },
              { name: 'Available', value: 'false' },
            ]}
            inputClass="py-2 px-3 text-sm"
          />

          <CustomSelectInput
            name="dealer"
            value={selectedDealer}
            onChange={(e) => handleDealerChange(e.target.value)}
            options={[
              { name: 'All Dealers', value: '' },
              ...dealerNames.map((d, i) => ({ name: d?.username || `Dealer ${i + 1}`, value: d?._id || '' })),
            ]}
            showSearch={true}
            inputClass="py-2 px-3 text-sm"
          />

          <CustomSelectInput
            name="agent"
            value={selectedAgent}
            onChange={(e) => { setSelectedAgent(e.target.value); setCurrentPage(1); }}
            options={[
              { name: 'All Agents', value: '' },
              ...agentNames.map((a, i) => ({ name: a?.username || `Agent ${i + 1}`, value: a?._id || '' })),
            ]}
            showSearch={true}
            inputClass="py-2 px-3 text-sm"
          />
        </div>

        <div className="flex items-center justify-end gap-2 shrink-0">
          <button
            onClick={() => {
              setSearch('');
              setDebouncedSearch(''); // clear now, don't wait out the debounce
              setStatus('');
              setSelectedVehicleClass('');
              setActivationFilter('');
              setActivationSortOrder('desc'); // reset sort direction
              setSelectedDealer('');
              setSelectedAgent('');
              setSelectedDate(null);
              setCurrentPage(1);
              setItemsPerPage(10); // pager back to page 1 at the default size
              // Reset the Agent dropdown back to all of this distributor's agents.
              API.GetAgentsNames()
                .then((res) => setAgentNames(Array.isArray(res?.data) ? res.data : []))
                .catch(() => setAgentNames([]));
            }}
            title="Reset Filters"
            aria-label="Reset Filters"
            className="w-full xl:w-[38px] h-[38px] border border-[#EE3B31] text-[#EE3B31] rounded-lg font-semibold transition-all duration-200 hover:bg-[#EE3B31] hover:text-white flex items-center justify-center shadow-sm hover:shadow cursor-pointer"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
        </div>
      </div>

      {/* Vehicle image modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="relative bg-white rounded-[20px] shadow-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => {
                    setModalOpen(false);
                    if (selectedIndex !== null) toggleVisibility(selectedIndex, selectedClass);
                  }}
                  className="text-gray-600 hover:text-red-500 p-1"
                >
                  <X size={18} />
                </button>
              </div>
              <h2 className="text-lg md:text-xl font-semibold mb-[4px] text-center pr-16">{vehicleData?.value}</h2>
              <p className="mb-[20px] text-xs md:text-sm text-center px-2">{vehicleData?.description}</p>
              <div className="relative w-full flex justify-center items-center py-8 px-4">
                <div className="relative bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 max-w-2xl w-full">
                  <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-red-500 rounded-tl-2xl opacity-20"></div>
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-red-500 rounded-br-2xl opacity-20"></div>
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-300 shadow-inner">
                    <img
                      src={vehicleData?.cardWebp}
                      alt={vehicleData?.name}
                      className="w-full h-auto object-contain rounded-lg transition-transform duration-500 hover:scale-[1.12]"
                    />
                  </div>
                  <div className="mt-6 flex justify-center">
                    <div className="h-1 w-24 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <CommonTable
        loading={isLoading}
        isPagination={true}
        columns={columns}
        data={inventoryData || []}
        emptyMessage={isLoading ? 'Loading...' : 'No inventory data found'}
        total={totalRecords || 0}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        totalPages={totalPages || 1}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        extraTableClass="bg-white rounded-b-2xl shadow-sm"
        hideDownload={false}
        onDownload={handleDownload}
        downloading={isDownloading}
      />

      {selectedTagRefId && (
        <TagCostInfoModal
          tagRefId={selectedTagRefId}
          isActivated={selectedTagActivated}
          initialTab={modalInitialTab}
          onClose={() => { setSelectedTagRefId(null); setSelectedTagActivated(false); }}
        />
      )}
    </div>
  );
};

export default DistributorMasterFASTag;
